<template>
  <div v-if="isVisible" class="booking-summary">
    <div class="summary-details">
      <div class="summary-grid">
        <div class="summary-row">
          <BookingCalendarIcon :primary-color="primaryColor" :secondary-color="secondaryColor" />
          <span class="summary-text">{{ formattedDate }}</span>
        </div>

        <div class="summary-row">
          <BookingClockIcon :primary-color="primaryColor" :secondary-color="secondaryColor" />
          <span class="summary-text">{{ time }}</span>
        </div>

        <div class="summary-row">
          <BookingLocationIcon :primary-color="primaryColor" :secondary-color="secondaryColor" />
          <span class="summary-text">{{ courtName }}</span>
        </div>

        <div class="summary-row">
          <BookingFlashIcon :primary-color="primaryColor" :secondary-color="secondaryColor" />
          <span class="summary-text">{{ duration }} мин</span>
        </div>
      </div>

      <div v-if="trainerName" class="summary-row-full">
        <img :src="profileIcon" alt="" class="summary-icon" />
        <span class="summary-text">с тренером {{ trainerName }}</span>
      </div>
    </div>

    <div class="summary-footer">
      <div class="price-section">
        <div class="total-price">{{ formatPrice(totalPrice) }}</div>
        <div class="price-duration">за {{ duration }} мин</div>
      </div>

      <button
        class="book-button"
        @click="handleBook"
      >
        Забронировать
      </button>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed } from 'vue'
import { formatCurrencyPrice } from '@/widgets/booking/utils/currency'
import profileIcon from '@/widgets/booking/assets/img/icons/profile-blue.svg'
import BookingCalendarIcon from '@/widgets/booking/components/icons/BookingCalendarIcon.vue'
import BookingClockIcon from '@/widgets/booking/components/icons/BookingClockIcon.vue'
import BookingLocationIcon from '@/widgets/booking/components/icons/BookingLocationIcon.vue'
import BookingFlashIcon from '@/widgets/booking/components/icons/BookingFlashIcon.vue'

const props = defineProps({
  date: {
    type: String,
    default: undefined
  },
  time: {
    type: String,
    default: undefined
  },
  courtName: {
    type: String,
    default: undefined
  },
  duration: {
    type: Number,
    default: undefined
  },
  trainerName: {
    type: String,
    default: undefined
  },
  totalPrice: {
    type: Number,
    default: undefined
  },
  currency: {
    type: String,
    default: 'RUB'
  },
  primaryColor: {
    type: String,
    default: '#3C67D6'
  },
  secondaryColor: {
    type: String,
    default: '#3C67D6'
  }
})

const emit = defineEmits(['book'])

const isVisible = computed(() => {
  return !!(props.date && props.time && props.courtName && props.duration && props.totalPrice)
})

const primaryColor = computed(() => props.primaryColor || '#3C67D6')
const secondaryColor = computed(() => props.secondaryColor || '#3C67D6')

const formattedDate = computed(() => {
  if (!props.date) return ''

  const date = new Date(props.date)
  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' })
  const dayMonth = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
  const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1).replace('.', '')
  return `${capitalizedWeekday}, ${dayMonth}`
})

const formatPrice = (price) => {
  return formatCurrencyPrice(price, props.currency)
}

const handleBook = () => {
  emit('book')
}
</script>

<style scoped lang="scss">
.booking-summary {
  margin-top: -30px;
  position: sticky;
  bottom: 0;
  width: 100%;
  background: #FFFFFF;
  border-radius: 20px;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.summary-details {
  max-width: 500px;
  margin: 0 auto;
  padding: 20px 32px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  row-gap: 8px;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: 4px;

  &:nth-child(2n) {
    justify-content: flex-end;
  }
}

.summary-row-full {
  display: flex;
  align-items: center;
  gap: 4px;
  grid-column: 1 / -1;
}

.summary-icon { width: 20px; height: 20px; flex-shrink: 0; }

.summary-text {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #757885;
}

.summary-footer {
  max-width: 500px;
  margin: 0 auto;
  padding: 0 32px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.price-section {
  display: flex;
  align-items: baseline;
  column-gap: 8px;
  row-gap: 0;
  flex-wrap: wrap;
}

.total-price {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 26px;
  font-weight: 700;
  line-height: 30px;
  color: #333333;
}

.price-duration {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #757885;
}

.book-button {
  width: 168px;
  height: 44px;
  background: var(--booking-widget-primary-color, #3C67D6);
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: #FFFFFF;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}
</style>
